import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import TipTapViewer from "../TipTapViewer";
import { useUser } from "../../context/UserContext";
import { TermosUsoService } from "../../service/termos_uso_service";

const termosUsoService = new TermosUsoService();

// Telas sem login: lá não há usuário para cobrar o aceite.
const ROTAS_PUBLICAS = ["/", "/rastreabilidade"];

/**
 * Cobra o aceite dos Termos de Uso do Distribuidor, como o Campo faz.
 *
 * Fica montado uma vez no App, fora das rotas, para aparecer em qualquer tela.
 * Verifica ao entrar e a cada troca de tela, então um termo publicado no admin
 * com o usuário já logado é cobrado na próxima navegação. Sem aceitar, o modal
 * não fecha.
 */
export default function TermosUsoCheck() {
	const { user, authStatus } = useUser();
	const { pathname } = useLocation();
	const [termo, setTermo] = useState(null);
	const [podeAceitar, setPodeAceitar] = useState(false);
	const [salvando, setSalvando] = useState(false);
	const contentRef = useRef(null);

	const logado = authStatus === "authenticated" && !ROTAS_PUBLICAS.includes(pathname);

	useEffect(() => {
		if (!logado || termo) return;
		let cancelado = false;

		const verificar = async () => {
			try {
				const ultimo = await termosUsoService.getLatest();
				if (!ultimo?.id) return;
				const { jaAceitou } = await termosUsoService.checkAcceptance(ultimo.id);
				if (!cancelado && !jaAceitou) {
					setPodeAceitar(false);
					setTermo(ultimo);
				}
			} catch (error) {
				console.error("Erro ao verificar os Termos de Uso:", error);
			}
		};

		verificar();
		return () => {
			cancelado = true;
		};
	}, [logado, pathname, user?.userId, termo]);

	// Saiu da sessão com o modal aberto: o próximo login verifica de novo.
	useEffect(() => {
		if (!logado) setTermo(null);
	}, [logado]);

	// Texto curto não tem rolagem: libera o botão depois que o modal abriu.
	useEffect(() => {
		if (!termo) return;
		const timer = setTimeout(() => {
			if (contentRef.current) {
				const { scrollHeight, clientHeight } = contentRef.current;
				if (scrollHeight <= clientHeight + 5) setPodeAceitar(true);
			}
		}, 200);
		return () => clearTimeout(timer);
	}, [termo]);

	const handleScroll = (e) => {
		const { scrollTop, scrollHeight, clientHeight } = e.target;
		if (scrollTop + clientHeight + 5 >= scrollHeight) setPodeAceitar(true);
	};

	const handleAceitar = async () => {
		setSalvando(true);
		try {
			await termosUsoService.accept(termo.id);
			setTermo(null);
		} catch (error) {
			console.error("Erro ao aceitar os Termos de Uso:", error);
			alert("Ocorreu um erro ao registrar seu aceite. Tente novamente.");
		} finally {
			setSalvando(false);
		}
	};

	return (
		<Dialog open={logado && !!termo} disableEscapeKeyDown maxWidth="md" fullWidth>
			<DialogTitle sx={{ fontWeight: "bold", textAlign: "center" }}>
				Nossos Termos de Uso foram atualizados
			</DialogTitle>

			<DialogContent dividers onScroll={handleScroll} ref={contentRef} sx={{ p: 3 }}>
				<Typography variant="body2" sx={{ mb: 3, fontWeight: "bold" }}>
					Para continuar a utilizar nossa plataforma, você precisa ler e aceitar os novos Termos de Uso.
				</Typography>

				<Box sx={{ p: 2, bgcolor: "#f9f9f9", borderRadius: 1, color: "#333" }}>
					{termo && <TipTapViewer content={termo.conteudo} />}
				</Box>
			</DialogContent>

			<DialogActions sx={{ p: 2 }}>
				<Button
					variant="contained"
					onClick={handleAceitar}
					disabled={!podeAceitar || salvando}
					fullWidth
					sx={{
						py: 1.5,
						fontWeight: "bold",
						bgcolor: "#1a9e8f",
						"&:hover": { bgcolor: "#158a7d" },
					}}
				>
					{podeAceitar ? "Li e aceito os Termos de Uso" : "Leia até o final para aceitar"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
